"use strict";
/**
 * THE CONSULTATION SPLIT — how much of a fee is the doctor's.
 *
 * Lives in `contracts` for the same reason `charge-tier-pricing.ts` and
 * `open-value-pricing.ts` do: the BROWSER shows the operator "Doctor ₹120 ·
 * Hospital ₹180" while they set a consultant's terms in Doctor Master, and the
 * SERVER posts those two figures as a revenue leg and a payable leg. A second
 * copy of this arithmetic in React is the specific defect this file prevents —
 * the two would agree for months and then disagree by a paisa on a percentage
 * that does not divide evenly, and the disagreement would surface as a journal
 * that does not add up to the invoice.
 *
 * WHY BASIS POINTS. A percentage share is stored as basis points (2500 =
 * 25.00%), so the split is exact integer arithmetic. A stored `33.33` cannot be
 * reconciled against a rupee total: a third of ₹100 is not 33.33% of ₹100, and
 * the difference is real money once it is multiplied by a year of consultations.
 *
 * WHY THE HOSPITAL TAKES THE REMAINDER. `Math.floor` on the doctor's side sends
 * every sub-paise fraction to the hospital. The doctor's share is a LIABILITY —
 * money the hospital owes a consultant — and rounding a liability up creates an
 * obligation against money that was never collected. Rounding it down leaves the
 * hospital holding a paisa it did collect. Only one of those is defensible.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.DOCTOR_SHARE_BASIS_POINTS_MAX = void 0;
exports.splitConsultationFee = splitConsultationFee;
/** 100.00% in basis points. A share can never exceed it. */
exports.DOCTOR_SHARE_BASIS_POINTS_MAX = 10_000;
/**
 * Splits a consultation fee between the consultant and the hospital.
 *
 * Total-preserving by construction: the hospital's share is computed as the
 * remainder and never independently, so the two legs always add back to
 * `feePaise` whatever the inputs. That property is what the posting engine
 * depends on, and it is asserted directly in the unit tests.
 *
 * Every degenerate input resolves to "the hospital keeps it" rather than
 * throwing: this runs inside a fee dialog with a patient at the counter, and a
 * mis-set master must not be able to stop a consultation being billed. The
 * masters refuse the bad states at the point they are entered; this refuses to
 * turn one into a negative leg if it gets through anyway.
 */
function splitConsultationFee(feePaise, mode, value) {
    if (!Number.isFinite(feePaise) || feePaise <= 0) {
        return { doctorSharePaise: 0, hospitalSharePaise: Math.max(Math.trunc(feePaise) || 0, 0) };
    }
    const fee = Math.trunc(feePaise);
    if (mode === null || value === null || !Number.isFinite(value)) {
        return { doctorSharePaise: 0, hospitalSharePaise: fee };
    }
    const raw = mode === 'PERCENT' ? Math.floor((fee * Math.trunc(value)) / exports.DOCTOR_SHARE_BASIS_POINTS_MAX) : Math.trunc(value);
    /**
     * Clamped rather than trusted, and this is not belt-and-braces.
     *
     * `mst_doctors_share_consistent_chk` refuses a flat share above the fee at
     * write time — but it checks the fee IN THAT ROW at THAT moment. Lowering the
     * fee later re-checks it, yet nothing stops a share arriving from an import,
     * a fixture, or a future code path that writes the columns directly. An
     * unclamped share above the fee posts a NEGATIVE hospital revenue leg: a
     * journal that balances perfectly and means nothing.
     */
    const doctorSharePaise = Math.max(0, Math.min(raw, fee));
    return { doctorSharePaise, hospitalSharePaise: fee - doctorSharePaise };
}
//# sourceMappingURL=consultation-split.js.map